import { useInCreateDialog } from 'components/create-dialog/CreateDialogContext';
import { CreateDialogRequiredBlock } from 'components/create-dialog/CreateDialogRequired';
import FeatureFlagged from 'components/featureflag/FeatureFlagged';
import FormRow from 'components/forms/FormRow';
import AddressInput from 'components/forms/inputs/rhf-inputs/AddressInput';
import MultiSelectInput from 'components/forms/inputs/rhf-inputs/MultiSelectInput';
import { useIsNewDesignInput } from 'components/forms/inputs/rhf-inputs/newDesignInput';
import TextInput from 'components/forms/inputs/rhf-inputs/TextInput';
import InfoTooltip from 'components/InfoTooltip';
import { FuzzySearchTableView } from 'hooks/useFuzzySearch';
import { PartyType, RelationshipTypeCode } from 'types/contacts';
import { CustomField } from 'types/customFields';
import { FeatureFlag } from 'types/features';
import { SegmentFields } from './SegmentFields';
import { ContactFormState } from './useContactForm';

/** Company details: official and unofficial names, registration number and address, business areas. */
export const CompanyFields = ({ form }: { form: ContactFormState }): JSX.Element => {
    const { control, setValue } = form.methods;
    const {
        businessAreaOptions,
        classifiers,
        contact,
        fuzzySearchStates,
        isBusinessAreaRequired,
        isRegistrationNumberMandatory: isRegCodeRequired,
        prefillName,
        relationshipType,
        searchHandlers,
        type,
        updateSearchState,
    } = form;
    const inCreateDialog = useInCreateDialog();
    const isNewDesign = useIsNewDesignInput();

    // In the create dialog a duplicates panel is drawn where the field it searches by is: the names are always
    // required, so theirs is always in Required.
    const nameDuplicates =
        type === 'add' ? (
            <CreateDialogRequiredBlock when>
                <FuzzySearchTableView
                    fuzzySearchResults={fuzzySearchStates.NAME.results}
                    partyType={PartyType.COMPANY}
                    isAccordionExpanded={fuzzySearchStates.NAME.isExpanded}
                    setIsAccordionExpanded={(expanded) => updateSearchState('NAME', { isExpanded: expanded })}
                    classifiers={classifiers}
                />
            </CreateDialogRequiredBlock>
        ) : null;

    return (
        <>
            <TextInput
                name="officialName"
                control={control}
                label="contacts.company.name"
                placeholder="contacts.company.name"
                required
                maxLength={150}
                onChange={searchHandlers?.name?.handler}
                onBlur={prefillName}
            />
            {inCreateDialog ? null : nameDuplicates}
            <TextInput
                name="name"
                control={control}
                label="contacts.unofficial-name"
                tooltip={<InfoTooltip text="contacts.tooltips.unofficial-name" />}
                // A sample value, not copy: kept out of the catalog, so a session replay masks it.
                placeholder={isNewDesign ? 'Acme' : 'forms.enter-name'}
                shouldTranslatePlaceholder={!isNewDesign}
                required
                maxLength={150}
            />
            {/* Required lays two fields to a row and the panel takes a whole one: under both names there, so
                that it does not part them. */}
            {inCreateDialog ? nameDuplicates : null}
            <FeatureFlagged
                action="show"
                mode="any"
                flag={[FeatureFlag.LOCALIZED_FIELDS, FeatureFlag.LOCALIZED_FIELDS_CONTACTS]}
            >
                <TextInput
                    name="officialLocalName"
                    control={control}
                    label="official-name-local"
                    placeholder="forms.enter-official-name-local"
                    maxLength={100}
                />
            </FeatureFlagged>
            <FormRow>
                <TextInput
                    name="regCode"
                    control={control}
                    label="reg-number"
                    placeholder="reg-number"
                    onChange={searchHandlers?.regCode?.handler}
                    required={isRegCodeRequired}
                />
                <TextInput name="website" control={control} label="website" placeholder="website" />
            </FormRow>
            {type === 'add' && (
                <CreateDialogRequiredBlock when={isRegCodeRequired}>
                    <FuzzySearchTableView
                        fuzzySearchResults={fuzzySearchStates.REGISTRATION_NUMBER.results}
                        partyType={PartyType.COMPANY}
                        isAccordionExpanded={fuzzySearchStates.REGISTRATION_NUMBER.isExpanded}
                        setIsAccordionExpanded={(expanded) =>
                            updateSearchState('REGISTRATION_NUMBER', { isExpanded: expanded })
                        }
                        classifiers={classifiers}
                    />
                </CreateDialogRequiredBlock>
            )}
            <MultiSelectInput
                control={control}
                name="businessAreas"
                label="business-areas"
                placeholder="business-areas"
                options={businessAreaOptions}
                setValue={setValue}
                getOptionLabel="label"
                getOptionIdentifier="value"
                sortDirection="asc"
                required={isBusinessAreaRequired}
            />
            <AddressInput
                label="registration-address"
                name="registrationAddress"
                control={control}
                classifiers={classifiers}
                customFields={contact?.registrationAddress?.customFields as CustomField[]}
                required={relationshipType === RelationshipTypeCode.CLIENT}
            />
            <FeatureFlagged
                action="show"
                mode="any"
                flag={[FeatureFlag.LOCALIZED_FIELDS, FeatureFlag.LOCALIZED_FIELDS_CONTACTS]}
            >
                <AddressInput
                    label="registration-address-local"
                    name="registrationLocalAddress"
                    control={control}
                    classifiers={classifiers}
                    customFields={contact?.registrationLocalAddress?.customFields as CustomField[]}
                />
            </FeatureFlagged>
            <SegmentFields form={form} />
        </>
    );
};
